import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import TopNavbar from "@/components/layout/TopNavbar";
import Footer from "@/components/home/Footer";
import { Button } from "@/components/ui/button";
import { OptimizedImage } from "@/components/ui";
import { Heart, Star, Calendar, Users } from "lucide-react";
import { useUnsplashImages } from "@/hooks/useUnsplashImages";
import { Link } from 'react-router-dom';

const PlayCenters = () => {
  const [heroImage, setHeroImage] = useState("");
  const { data: playImages } = useUnsplashImages("children indoor playground", 1);
  
  useEffect(() => {
    if (playImages && playImages.length > 0) {
      setHeroImage(playImages[0].urls.regular);
    }
  }, [playImages]);

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Play Center Management Software - Hivetivity</title>
        <meta name="description" content="Streamline your play center operations with Hivetivity. Manage bookings, events, and parent communications all in one place." />
      </Helmet>

      <TopNavbar />

      <main>
        {/* Hero Section */}
        <section className="relative py-20 bg-gradient-to-b from-purple-50 to-white">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="text-4xl md:text-5xl font-bold text-[#5a5a59] mb-6">
                  Level Up Your Play Center Management
                </h1>
                <p className="text-xl text-gray-600 mb-8">
                  Focus on creating magical play experiences while we handle the administrative tasks with our comprehensive platform.
                </p>
                <Link to="/business-trial">
                  <Button className="bg-purple-500 hover:bg-purple-600 text-white px-8 py-6 text-lg">
                    Start Free Trial
                  </Button>
                </Link>
              </div>
              <div className="relative">
                {heroImage ? (
                  <OptimizedImage 
                    src={heroImage}
                    alt="Children having fun at play center"
                    width={600}
                    height={400}
                    className="rounded-2xl shadow-xl"
                    objectFit="cover"
                  />
                ) : (
                  <div className="rounded-2xl bg-gray-200 w-full h-[400px] animate-pulse flex items-center justify-center">
                    <p className="text-gray-500">Loading image...</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center text-[#5a5a59] mb-12">
              Everything Your Play Center Needs
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="p-6 rounded-xl bg-purple-50 hover:bg-purple-100 transition-colors">
                <Calendar className="w-12 h-12 text-purple-500 mb-4" />
                <h3 className="text-xl font-semibold text-[#5a5a59] mb-2">Party Booking</h3>
                <p className="text-gray-600">Manage birthday parties and special events.</p>
              </div>
              <div className="p-6 rounded-xl bg-purple-50 hover:bg-purple-100 transition-colors">
                <Users className="w-12 h-12 text-purple-500 mb-4" />
                <h3 className="text-xl font-semibold text-[#5a5a59] mb-2">Capacity Control</h3>
                <p className="text-gray-600">Monitor and manage visitor numbers.</p>
              </div>
              <div className="p-6 rounded-xl bg-purple-50 hover:bg-purple-100 transition-colors">
                <Star className="w-12 h-12 text-purple-500 mb-4" />
                <h3 className="text-xl font-semibold text-[#5a5a59] mb-2">Member Management</h3>
                <p className="text-gray-600">Handle memberships and regular visitors.</p>
              </div>
              <div className="p-6 rounded-xl bg-purple-50 hover:bg-purple-100 transition-colors">
                <Heart className="w-12 h-12 text-purple-500 mb-4" />
                <h3 className="text-xl font-semibold text-[#5a5a59] mb-2">Safety First</h3>
                <p className="text-gray-600">Track maintenance and safety checks.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonial Section */}
        <section className="py-20 bg-purple-50">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl font-bold text-[#5a5a59] mb-8">
                Trusted by Play Centers
              </h2>
              <blockquote className="text-xl text-gray-600 italic mb-6">
                "Hivetivity has made managing our play center so much easier. The booking system and capacity management features are game-changers."
              </blockquote>
              <p className="font-semibold text-[#5a5a59]">
                - Sarah Anderson, Play World Owner
              </p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold text-[#5a5a59] mb-6">
              Ready to Transform Your Play Center?
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Join successful play centers using Hivetivity to create magical play experiences.
            </p>
            <Link to="/business-trial">
              <Button className="bg-purple-500 hover:bg-purple-600 text-white px-8 py-6 text-lg">
                Start Your Free Trial Today
              </Button>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PlayCenters;
